import { Box, Menu, Text } from 'grommet';
import { Add } from 'grommet-icons';
import React, { useMemo } from 'react';

import CoverageTagChip from '@/features/coverage/components/coverage-tag-chip';
import { useAddPackagingCoverageTag, useRemovePackagingCoverageTag } from '@/features/drugs/api/use-packaging-coverage-tag-mutations';
import { CoverageTag } from '@/features/drugs/types/coverage-tag';
import { Drug } from '@/features/drugs/types/drug';
import { DrugPackaging } from '@/features/drugs/types/drug-packaging';

type PackagingCoverageTagsCellProps = {
  drug: Drug;
  packaging: DrugPackaging;
  canManageCoverageTags: boolean;
};

export const PackagingCoverageTagsCell = ({ drug, packaging, canManageCoverageTags }: PackagingCoverageTagsCellProps) => {
  // only show non-default coverage tags at this level
  const coverageTags = useMemo(() => {
    return packaging?.coverageTags ?? [];
  }, [packaging?.coverageTags]);

  const availableCoverageTags = useMemo(() => {
    const allCoverageTags = drug?.coverageTags?.filter(it => !it.isDefault) ?? [];
    return allCoverageTags.filter(it => !coverageTags.find(ct => ct.id === it.id));
  }, [drug?.coverageTags, coverageTags]);

  const addCoverageTag = useAddPackagingCoverageTag();
  const removeCoverageTag = useRemovePackagingCoverageTag();
  const isPending = addCoverageTag.isPending || removeCoverageTag.isPending;

  const handleAddCoverageTag = (coverageTag: CoverageTag) => {
    addCoverageTag.mutate({ drugId: drug.id, ndc: packaging.ndc, coverageTag });
  };

  const handleRemoveCoverageTag = (coverageTag: CoverageTag) => {
    removeCoverageTag.mutate({ drugId: drug.id, ndc: packaging.ndc, coverageTag });
  };

  const menuItems = availableCoverageTags.length > 0
    ? [
      availableCoverageTags.map(tag => ({
        label: tag.name,
        onClick: () => handleAddCoverageTag(tag)
      }))
    ]
    : [];

  return (
    <Box direction="row" gap="small" justify="between" align="center" width="100%">
      {coverageTags.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
          {coverageTags.map(tag => (
            <CoverageTagChip
              key={tag.id}
              coverageTag={tag}
              dismissible={canManageCoverageTags}
              onDismiss={() => handleRemoveCoverageTag(tag)}
            />
          ))}
        </div>
      ) : (
        <Text>&mdash;</Text>
      )}
      {canManageCoverageTags && (
        <Menu
          label={<Add/>}
          icon={false}
          tip="Add Coverage Tag"
          a11yTitle="Add Coverage Tag"
          items={menuItems}
          disabled={isPending}
          dropAlign={{ top: 'bottom' }}
        />
      )}
    </Box>
  );
};
